import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { CheckboxList } from '../SettingsViews/SettingsComponents/CheckboxList'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import './ConversionModal.scss'
import RightArrow from '@tecuity/sos-icons/react/right-arrow'
import Revert from '@tecuity/sos-icons/react/revert'
import { Spinner } from '../../Loaders/Loaders'
import * as api from '../../../requests'
import { setErrorToast, setSuccessToast } from '../../../actions'

const ConversionModal = ({
  selectedRow,
  recordTypeOptions,
  recordSubTypeOptions,
  businessStatusTypeOptions = [],
  getRecordTypeName,
  getRecordSubTypeName,
  closeModal,
  receiptItemTypes,
  appReports = [],
  filingType,
}) => {
  const dispatch = useDispatch()
  const [xref, setXref] = useState({ ...selectedRow })
  const [reportIds, setReportIds] = useState([])
  const [receiptItemTypeIds, setReceiptItemTypeIds] = useState([])
  const [initialSubTypeXref, setInitialSubTypeXref] = useState([])
  const [convertedSubTypeXref, setConvertedSubTypeXref] = useState([])
  const [loading, setLoading] = useState(false)

  const revert = () => {
    setXref({ ...selectedRow })
    setReportIds(() => [...selectedRow.ReportIds])
    setReceiptItemTypeIds(() => [...selectedRow.ReceiptItemTypeIds])
  }

  useEffect(() => {
    revert()
  }, [selectedRow])

  const getRecordSubTypeXref = (recordTypeId, setSubTypeXref) => {
    //api call
    setLoading(true)
    api.GET_BUSINESS_RECORD_SUB_TYPE_XREFS_BY_RECORD_TYPE(
      recordTypeId,
      (res) => {
        // console.log(res.BusinessRecordSubTypeIds)
        setSubTypeXref(res.BusinessRecordSubTypeIds)
        setLoading(false)
      },
      (err) => {
        console.error('retrieving', err)
        setLoading(false)
      }
    )
  }

  useEffect(() => {
    getRecordSubTypeXref(xref.InitialRecordTypeId, setInitialSubTypeXref)
  }, [xref.InitialRecordTypeId])

  useEffect(() => {
    getRecordSubTypeXref(xref.ConvertedRecordTypeId, setConvertedSubTypeXref)
  }, [xref.ConvertedRecordTypeId])

  const filteredInitialSubTypes = useMemo(() => {
    return recordSubTypeOptions.filter((opt) => initialSubTypeXref.includes(opt.value))
  }, [recordSubTypeOptions, initialSubTypeXref])

  const filteredConvertedSubTypes = useMemo(() => {
    return recordSubTypeOptions.filter((opt) => convertedSubTypeXref.includes(opt.value))
  }, [recordSubTypeOptions, convertedSubTypeXref])

  const saveXref = () => {
    api.SAVE_SETTINGS_BUSINESS_CONVERSION_TYPE_XREF(
      {
        ...xref,
        ConversionFilingTypeId: Number(filingType),
        ReceiptItemTypeIds: receiptItemTypeIds,
        ReportIds: reportIds,
      },
      xref.ConversionRecordTypeXrefId ? 'POST' : 'PUT',
      () => {
        dispatch(setSuccessToast('Successfully saved Conversion Xref'))
        closeModal(true)
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to Save XRef'))
      }
    )
  }

  return (
    <div className="conversion-modal">
      <div className="from-to-row">
        <div className="from-to-column from">
          <SelectBox
            label="Initial Record Type"
            value={xref.InitialRecordTypeId}
            onChange={(val) => {
              setXref({
                ...xref,
                InitialRecordTypeId: Number(val),
                InitialRecordTypeName: getRecordTypeName(Number(val)),
                InitialRecordSubTypeId: null,
              })
            }}
            options={recordTypeOptions}
          />
          <SelectBox
            label="Initial Record Sub Type"
            value={xref.InitialRecordSubTypeId}
            onChange={(val) => {
              setXref({
                ...xref,
                InitialRecordSubTypeId: Number(val),
                InitialRecordSubTypeName: getRecordSubTypeName(Number(val)),
              })
            }}
            options={filteredInitialSubTypes}
          />
        </div>
        <div className="from-to-column arrow">
          <RightArrow />
          {loading && (
            <div className="conversion-spinner">
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )}
        </div>
        <div className="from-to-column to">
          <SelectBox
            label="Converted Record Type"
            value={xref.ConvertedRecordTypeId}
            onChange={(val) => {
              setXref({
                ...xref,
                ConvertedRecordTypeId: Number(val),
                ConvertedRecordTypeName: getRecordTypeName(Number(val)),
                ConvertedRecordSubTypeId: null,
              })
            }}
            options={recordTypeOptions}
          />
          <SelectBox
            label="Converted Record Sub Type"
            value={xref.ConvertedRecordSubTypeId}
            onChange={(val) => {
              setXref({
                ...xref,
                ConvertedRecordSubTypeId: Number(val),
                ConvertedRecordSubTypeName: getRecordSubTypeName(Number(val)),
              })
            }}
            options={filteredConvertedSubTypes}
          />
        </div>
      </div>
      <div className="conversion-center">
        <SelectBox
          label="Status after Approval"
          value={xref.PostConversionBusinessStatusTypeId}
          onChange={(val) => {
            setXref({
              ...xref,
              PostConversionBusinessStatusTypeId: Number(val),
            })
          }}
          options={businessStatusTypeOptions}
        />
        <CheckboxList
          modalMaxHeight
          checkListOnly
          label="Receipt Item Types"
          items={receiptItemTypes
            .filter((type) => type.ReceiptItemCategoryTypeId === 2)
            .map((type) => {
              return {
                label: type.Name,
                id: type.Id,
              }
            })}
          checkedList={receiptItemTypeIds || []}
          setActiveChanges={(newTypes) => {
            // console.log(newTypes)
            setReceiptItemTypeIds([...newTypes])
          }}
        />
        <CheckboxList
          modalMaxHeight
          checkListOnly
          label={'Generated Reports'}
          items={appReports
            .map((appReport) => ({ id: appReport.Id, label: appReport.Name }))
            .sort((a, b) => {
              return a.label > b.label ? 1 : -1
            })}
          checkedList={reportIds || []}
          setActiveChanges={(ids) => {
            // console.log(ids)
            setReportIds([...ids])
          }}
        />
      </div>
      <ContextBar elevation="standard" className="modal-context-bar">
        <ContextButton
          variant="danger"
          // disabled={!isDirty}
          leftIcon={
            <div className="checkmark-wrapper left">
              <Revert />
            </div>
          }
          onClick={revert}
        >
          Revert
        </ContextButton>
        <ContextButton
          variant="secondary"
          // disabled={!isDirty}
          onClick={() => {
            setXref({})
            setReceiptItemTypeIds([])
            setReportIds([])
            closeModal(false)
          }}
        >
          Close
        </ContextButton>
        <ContextButton
          variant="primary"
          // disabled={!isDirty}
          rightIcon={
            <div className="checkmark-wrapper right">
              <RightArrow />
            </div>
          }
          onClick={saveXref}
        >
          Save
        </ContextButton>
      </ContextBar>
    </div>
  )
}

export default ConversionModal
